import { Plus, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Button, DataTable } from "../../../components/ui";
import { TableActions } from "../../../components/ui/TableActions";
import { TABLE_BACKGROUNDS } from "../../../constants/tableBackgrounds";
import AddInstallationMemberModal from "../modals/AddInstallationMemberModal";
import { installationMemberService } from "../services/installationMemberService";

export default function InstallationMembersTab() {
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [viewingMember, setViewingMember] = useState(null);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const res = await installationMemberService.getMembers();
      if (res.success) {
        setMembers(res.data);
      }
    } catch (error) {
      toast.error("Failed to fetch installation members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const openCreate = () => {
    setEditingMember(null);
    setViewingMember(null);
    setModalOpen(true);
  };

  const openEdit = (member) => {
    setEditingMember(member);
    setViewingMember(null);
    setModalOpen(true);
  };

  const openView = (member) => {
    setEditingMember(null);
    setViewingMember(member);
    setModalOpen(true);
  };

  const toggleDisable = async (id) => {
    const memberToToggle = members.find(m => m._id === id);
    if (!memberToToggle) return;

    try {
      const newStatus = memberToToggle.status === 'Active' ? 'Inactive' : 'Active';
      const res = await installationMemberService.updateMember(id, { ...memberToToggle, status: newStatus });
      if (res.success) {
        toast.success(`Member marked as ${newStatus}`);
        setMembers(members.map(m => m._id === id ? { ...m, status: newStatus } : m));
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const saveMember = async (memberData) => {
    try {
      if (editingMember) {
        const res = await installationMemberService.updateMember(editingMember._id, memberData);
        if (res.success) {
          toast.success("Installation member updated successfully");
          setMembers(members.map(m => m._id === editingMember._id ? res.data : m));
        }
      } else {
        const res = await installationMemberService.createMember(memberData);
        if (res.success) {
          toast.success("Installation member created successfully");
          setMembers([res.data, ...members]);
        }
      }
      setModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Operation failed");
    }
  };

  const filteredMembers = members.filter(item => 
    (item.name || "").toLowerCase().includes(search.toLowerCase()) || 
    (item.memberId || "").toLowerCase().includes(search.toLowerCase()) ||
    (item.role || "").toLowerCase().includes(search.toLowerCase()) ||
    (item.phone || "").toLowerCase().includes(search.toLowerCase()) ||
    (item.address?.city || "").toLowerCase().includes(search.toLowerCase()) ||
    (item.address?.district || "").toLowerCase().includes(search.toLowerCase()) ||
    (item.address?.state || "").toLowerCase().includes(search.toLowerCase()) ||
    (item.address?.pincode || "").toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      header: "Member ID",
      render: (item) => <span className="text-[11px] font-bold text-slate-700 tracking-wider uppercase">{item.memberId || "—"}</span>
    },
    {
      header: "Name",
      render: (item) => <span className="text-slate-800 font-semibold text-xs">{item.name || "—"}</span>
    },
    {
      header: "Role",
      render: (item) => (
        <span className="text-[11px] font-medium text-slate-700 bg-slate-100 px-2.5 py-1 rounded-md whitespace-nowrap">
          {item.role || "General"}
        </span>
      )
    },
    {
      header: "Phone",
      render: (item) => <span className="text-slate-600 text-[11px] font-medium whitespace-nowrap">{item.phone || "—"}</span>
    },
    {
      header: "Address",
      render: (item) => {
        const fullAddr = [
          item.address?.lane1,
          item.address?.lane2,
          item.address?.city,
          item.address?.district,
          item.address?.state,
          item.address?.pincode
        ].filter(Boolean).join(", ");
        return (
          <div 
            className="text-slate-600 text-[11px] leading-relaxed max-w-[280px] truncate"
            title={fullAddr}
          >
            {fullAddr || "—"}
          </div>
        );
      }
    },
    {
      header: "Status",
      render: (item) => {
        const isActive = item.status === "Active";
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${isActive
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200/30'
                : 'bg-slate-50 text-slate-500 border-slate-200/30'
              }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            {item.status}
          </span>
        );
      }
    },
    {
      header: "Created Date & Time",
      className: "w-36 text-center",
      render: (item) => <span className="text-[11px] font-semibold text-slate-600 whitespace-nowrap">{item.createdAt ? new Date(item.createdAt).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : "—"}</span>
    },
    {
      header: "Actions",
      className: "text-center w-32",
      render: (item) => {
        const isInactiveStatus = item.status === "Inactive";
        return (
          <TableActions
            onView={() => openView(item)}
            onEdit={() => openEdit(item)}
            onToggle={() => toggleDisable(item._id)}
            isActive={item.status === 'Active'}
            disabled={isInactiveStatus}
          />
        );
      }
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-black text-slate-800 tracking-tight leading-none mb-2">
            Installation Members
          </h3>
          <p className="text-[13px] text-slate-500 font-medium">
            Manage installation team members, roles, contact and address details
          </p>
        </div>
        <Button
          onClick={openCreate}
          variant="orange"
          size="md"
          leftIcon={<Plus size={16} />}
        >
          Add Member
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members by name, role, phone..."
            className="w-full h-9 pl-9 pr-4 text-xs bg-slate-50 border border-slate-200 rounded-md outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
          />
          <svg className="absolute left-3 top-2.5 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
        </div>
      </div>

      <div className="card overflow-hidden relative min-h-[200px]">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm z-10">
            <Loader2 className="w-6 h-6 animate-spin text-brand-blue" />
          </div>
        ) : null}
        <DataTable
          columns={columns}
          data={filteredMembers}
          backgroundImage={TABLE_BACKGROUNDS.teams}
          isRowInactive={(row) => row.status === 'Inactive'}
          getRowClassName={(row) => row.status === 'Inactive' ? 'bg-slate-100/90 border-y border-slate-200/80' : 'hover:bg-slate-50/50'}
        />
      </div>

      {modalOpen && (
        <AddInstallationMemberModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={saveMember}
          initialData={editingMember || viewingMember}
          isViewOnly={!!viewingMember}
        />
      )}
    </div>
  );
}
